import apiClient from './apiClient';
import {
  JoinRoomResponse,
  LiveAnswerResult,
  LiveQuizRatingResult,
  LiveRoomState,
  LiveRoomStatus,
  UpcomingLiveQuizzesResponse
} from '../types/liveQuiz';

/**
 * Current room state.
 *
 * Also the polling fallback when EventSource cannot connect — it returns exactly
 * the payload the stream pushes, so the hook can treat both identically.
 */
export const getRoomState = async (roomId: number): Promise<LiveRoomState> => {
  const response = await apiClient.get(`/api/quizzes/live/${roomId}/state`);
  return response.data;
};

/** Enter a room, or re-enter after a dropped connection. */
export const joinRoom = async (roomId: number): Promise<JoinRoomResponse> => {
  const response = await apiClient.post(`/api/quizzes/live/${roomId}/join`);
  return response.data;
};

/** Presence heartbeat. Also nudges the room forward when it is otherwise idle. */
export const sendHeartbeat = async (roomId: number): Promise<LiveRoomState> => {
  const response = await apiClient.post(`/api/quizzes/live/${roomId}/heartbeat`);
  return response.data;
};

/**
 * Answer the current question.
 *
 * Sends no timing: the server measures elapsed time from the room's own phase
 * start, because a live result drives a public rating and cannot be trusted to
 * the browser.
 */
export const submitLiveAnswer = async (
  roomId: number,
  questionId: number,
  optionId: number
): Promise<LiveAnswerResult> => {
  const response = await apiClient.post(`/api/quizzes/live/${roomId}/answer`, {
    questionId,
    optionId
  });
  return response.data;
};

/**
 * The viewer's placement, score and rating change for a finished room.
 *
 * Per-user by construction — the broadcaster writes one identical frame to
 * every subscriber over the stream, so this cannot ride it and must be
 * fetched separately.
 */
export const getMyLiveResult = async (roomId: number): Promise<LiveQuizRatingResult> => {
  const response = await apiClient.get(`/api/quizzes/live/${roomId}/my-result`);
  return response.data;
};

/** Stop a room permanently. Admin only. */
export const abortRoom = async (roomId: number): Promise<{ roomId: number; status: string }> => {
  const response = await apiClient.post(`/api/quizzes/live/${roomId}/abort`);
  return response.data;
};

/**
 * RSVP to a live quiz — visibility-only, pinning it to the student's
 * dashboard. Keyed on the quiz, not the room: a room may not exist yet
 * when a student signs up, and a reschedule can abandon/recreate the room
 * without touching a signup keyed this way. Idempotent on the backend, so
 * a repeat call is not an error.
 */
export const rsvpToLiveQuiz = async (quizId: number): Promise<void> => {
  await apiClient.post(`/api/quizzes/live/signups/${quizId}`);
};

/** Cancel a previous RSVP. Also idempotent — cancelling twice is a no-op. */
export const cancelRsvp = async (quizId: number): Promise<void> => {
  await apiClient.delete(`/api/quizzes/live/signups/${quizId}`);
};

/**
 * Live/upcoming events for the discovery surfaces (dashboard banner, quizzes
 * live badge). Unpaginated and `Cache-Control: no-store` on the backend —
 * it's polled frequently and has a lazy room-creation side effect, so it
 * cannot ride the cacheable, paginated quiz list.
 */
export const getUpcomingLiveQuizzes = async (): Promise<UpcomingLiveQuizzesResponse> => {
  const response = await apiClient.get('/api/quizzes/live/upcoming');
  return response.data;
};

/** One row of the admin room list — a room plus the quiz and counts it needs to render without a join. */
export interface AdminLiveRoomRow {
  roomId: number;
  quizId: number;
  quizTitle: string;
  status: LiveRoomStatus;
  currentQuestionIndex: number;
  totalQuestions: number;
  scheduledStartAt: string;
  lobbyOpensAt: string;
  phaseEndsAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  participantCount: number;
  playedCount: number;
  activeCount: number;
  /** How many students RSVPed — the number the admin actually wants before a scheduled event. */
  signupCount: number;
  /** 0 here is a real outcome (field under three), not missing data. */
  ratedCount: number;
  /** No recent heartbeat — the room may be stuck. */
  isStale: boolean;
}

export interface AdminLiveRoomsResponse {
  total: number;
  page: number;
  limit: number;
  rooms: AdminLiveRoomRow[];
}

export interface AdminLiveRoomsParams {
  status?: string;
  page?: number;
  limit?: number;
}

/** Admin room list, tenant-filtered server-side. */
export const getAdminLiveRooms = async (
  params: AdminLiveRoomsParams = {}
): Promise<AdminLiveRoomsResponse> => {
  const response = await apiClient.get('/api/quizzes/live/admin/rooms', { params });
  return response.data;
};

/** "Open the lobby now" for a scheduled quiz — creates or reopens its room ahead of `startsAt`. */
export const openRoomNow = async (quizId: number): Promise<{ roomId: number; status: string }> => {
  const response = await apiClient.post(`/api/quizzes/live/admin/quizzes/${quizId}/room`);
  return response.data;
};

/**
 * Absolute URL for the SSE stream.
 *
 * EventSource cannot set an Authorization header, so the token rides as a query
 * parameter. Built here rather than in the hook so the base URL logic stays in
 * the service layer alongside apiClient.
 */
export const getStreamUrl = (roomId: number, token: string): string => {
  const base = apiClient.defaults.baseURL ?? '';
  return `${base}/api/quizzes/live/${roomId}/stream?token=${encodeURIComponent(token)}`;
};
